// frontend/src/pages/admin/AdminCreatorDetail.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Star, Ban, Pause, Play, Archive } from 'lucide-react';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Metric = ({ label, value }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4">
    <p className="text-xs text-gray-400 uppercase tracking-wide">{label}</p>
    <p className="text-xl font-bold text-gray-900 mt-1">{value ?? 0}</p>
  </div>
);

const AdminCreatorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const isSuperAdmin = (() => {
    try { return JSON.parse(localStorage.getItem('admin_user') || '{}').role === 'super_admin'; }
    catch { return false; }
  })();

  const load = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('admin_token');
    const r = await fetch(`${API_URL}/api/admin/marketplace/creators/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.ok) setC(await r.json());
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (path, body, method = 'PATCH') => {
    setBusy(true);
    const token = localStorage.getItem('admin_token');
    try {
      const r = await fetch(`${API_URL}/api/admin/marketplace/creators/${id}${path}`, {
        method, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        alert(typeof d.detail === 'string' ? d.detail : 'Action failed');
      } else {
        await load();
      }
    } finally { setBusy(false); }
  };

  const statusAction = (action) => {
    const reason = ['suspend', 'ban', 'archive'].includes(action)
      ? window.prompt(`Reason for ${action} (optional):`) : null;
    act('/status', { action, reason });
  };

  const viewId = (side) => {
    const token = localStorage.getItem('admin_token');
    // Open the admin-only image stream in a new tab with the token as a query param fallback.
    window.open(`${API_URL}/api/admin/marketplace/creators/${id}/id-image/${side}?token=${token}`, '_blank');
  };

  if (loading) return <AdminLayout><div className="h-48 flex items-center justify-center"><div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;
  if (!c) return <AdminLayout><p className="text-sm text-gray-500">Creator not found.</p></AdminLayout>;

  const v = c.verification || { status: 'none' };

  return (
    <AdminLayout>
      <button onClick={() => navigate('/admin/marketplace/creators')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to creators
      </button>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            {c.featured_creator && <Star className="w-5 h-5 text-amber-400 fill-amber-400" />}
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                {c.display_name}
                {v.status === 'verified' && <ShieldCheck className="w-5 h-5 text-blue-600" />}
              </h1>
              <p className="text-sm text-gray-400">{c.email}</p>
              <p className="text-xs text-gray-400 mt-1">
                Account: <span className="font-semibold">{c.account_status}</span> · Risk: <span className="font-semibold">{Math.round(c.creator_risk_score)}</span>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button disabled={busy} onClick={() => statusAction('suspend')} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100"><Pause className="w-3.5 h-3.5" />Suspend</button>
            <button disabled={busy} onClick={() => statusAction('unsuspend')} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100"><Play className="w-3.5 h-3.5" />Unsuspend</button>
            <button disabled={busy} onClick={() => statusAction('ban')} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-red-50 text-red-600 hover:bg-red-100"><Ban className="w-3.5 h-3.5" />Ban</button>
            {isSuperAdmin && <button disabled={busy} onClick={() => statusAction('unban')} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100">Unban</button>}
            <button disabled={busy} onClick={() => statusAction('archive')} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"><Archive className="w-3.5 h-3.5" />Archive</button>
            <button disabled={busy} onClick={() => act('/featured', { featured: !c.featured_creator })} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100"><Star className="w-3.5 h-3.5" />{c.featured_creator ? 'Unfeature' : 'Feature'}</button>
          </div>
        </div>
      </div>

      {/* Verification panel */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
        <h2 className="font-semibold text-gray-900 mb-3">Verification — <span className="capitalize">{v.status}</span></h2>
        {v.status === 'none' && <p className="text-sm text-gray-500">This creator has not submitted verification.</p>}
        {v.status !== 'none' && (
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">ID type: <span className="text-gray-900">{v.id_type || '—'}</span></p>
              <p className="text-gray-500">Country: <span className="text-gray-900">{v.country || '—'}</span></p>
              <p className="text-gray-500">ID last 4: <span className="text-gray-900">{v.id_number_last4 || '—'}</span></p>
              {v.rejection_reason && <p className="text-red-600 mt-1">Rejected: {v.rejection_reason}</p>}
            </div>
            <div className="flex gap-2 flex-wrap items-start">
              {v.has_id_front && <button onClick={() => viewId('front')} className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">View ID front</button>}
              {v.has_id_back && <button onClick={() => viewId('back')} className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">View ID back</button>}
            </div>
          </div>
        )}
        {v.status === 'pending' && (
          <div className="flex gap-2 mt-4">
            <button disabled={busy} onClick={() => act('/verification', { action: 'verify' })} className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700">Approve verification</button>
            <button disabled={busy} onClick={() => { const reason = window.prompt('Rejection reason:'); if (reason) act('/verification', { action: 'reject', rejection_reason: reason }); }} className="px-4 py-2 text-sm rounded-lg bg-red-50 text-red-600 hover:bg-red-100">Reject</button>
          </div>
        )}
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Metric label="Templates" value={c.metrics?.templates_count} />
        <Metric label="Published" value={c.metrics?.published_count} />
        <Metric label="Rejected" value={c.rejected_templates_count} />
        <Metric label="Sales" value={c.metrics?.sales_count} />
        <Metric label="Followers" value={c.metrics?.followers_count} />
        <Metric label="Reviews" value={c.metrics?.review_count} />
        <Metric label="Open creator reports" value={c.open_creator_reports} />
        <Metric label="Open template reports" value={c.open_template_reports} />
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <h2 className="font-semibold text-gray-900 mb-3">Timeline</h2>
        {(!c.timeline || c.timeline.length === 0) ? (
          <p className="text-sm text-gray-500">No activity yet.</p>
        ) : (
          <ul className="space-y-2">
            {c.timeline.map((e, i) => (
              <li key={i} className="flex items-center gap-3 text-sm">
                <span className="w-2 h-2 rounded-full bg-violet-400" />
                <span className="text-gray-700 capitalize">{e.type.replace(/_/g, ' ')}</span>
                <span className="text-gray-400 ml-auto">{e.at ? new Date(e.at).toLocaleDateString() : ''}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCreatorDetail;
